/**
 * Asks the user why they download data before a CSV download starts.
 *
 * Each CSV download in the recruiter app opens this dialog first. The dialog is presentational and
 * lives in `@modernloop/shared`, so it cannot access data: the caller owns the download request and
 * the privacy audit log call.
 *
 * 1. The dialog shows which data the user is about to download (`dataDescription`).
 * 2. The user types a reason. `parseDownloadReason` parses the text on each change.
 * 3. The download button stays disabled until the text parses.
 * 4. A click on the download button calls `onDownload` with the parsed `DownloadReason`.
 *
 * The reason input clears each time the dialog opens, so a user cannot reuse an old reason by
 * accident. The parser is in `DownloadReason.ts`.
 */

import { Dialog } from '../Dialog';
import { DownloadReason, MAX_DOWNLOAD_REASON_LENGTH, parseDownloadReason } from './DownloadReason';
import { DownloadIcon } from '#icons';
import { useTranslation } from '@modernloop/i18n';
import { TextField } from '@mui/material';
import { useState } from 'react';

/** Props for `DownloadReasonDialog`. The caller owns the open state and the download request. */
export type DownloadReasonDialogProps = {
  /** Whether the dialog is open. */
  open: boolean;

  /**
   * Localized name of the data to download, for example "Privacy audit log, January 2026".
   * The dialog shows it below the title.
   */
  dataDescription: string;

  /** Whether the download request is in progress. The download button shows a spinner. */
  isDownloading?: boolean;

  /**
   * Starts the download. The caller gives `downloadReason` to the download endpoint, which gives
   * it to the privacy audit log action. The caller closes the dialog when the download completes.
   */
  onDownload: (downloadReason: DownloadReason) => void;

  /** Closes the dialog without a download. The cancel button and the close icon call it. */
  onClose: () => void;
};

/** Collects a download reason, then gives the parsed reason to `onDownload`. */
export const DownloadReasonDialog = ({
  open,
  dataDescription,
  isDownloading = false,
  onDownload,
  onClose,
}: DownloadReasonDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const [rawReason, setRawReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  // Clears the reason when the dialog opens again. This adjusts state during render and not in
  // an effect, as the React docs recommend for a reset on a prop change.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setRawReason('');
    }
  }

  const parsedReason = parseDownloadReason(rawReason);

  const handleDownload = () => {
    if (parsedReason.isOk()) {
      onDownload(parsedReason.value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('common:shared.downloadReasonDialog.title')}
      subTitle={dataDescription}
      maxWidth="sm"
      dataTestId="DownloadReasonDialog"
      cancelOptions={{
        label: t('common:shared.actions.cancel'),
        isDisabled: isDownloading,
        dataTestId: 'DownloadReasonDialog-Cancel',
        onClick: onClose,
      }}
      submitOptions={{
        label: t('common:shared.downloadReasonDialog.download'),
        startIcon: <DownloadIcon />,
        isDisabled: parsedReason.isErr(),
        isLoading: isDownloading,
        dataTestId: 'DownloadReasonDialog-Download',
        onClick: handleDownload,
      }}
    >
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        required
        disabled={isDownloading}
        label={t('common:shared.downloadReasonDialog.reasonLabel')}
        placeholder={t('common:shared.downloadReasonDialog.reasonPlaceholder')}
        helperText={t('common:shared.downloadReasonDialog.reasonHelperText')}
        value={rawReason}
        onChange={(event) => setRawReason(event.target.value)}
        inputProps={{
          maxLength: MAX_DOWNLOAD_REASON_LENGTH,
          'data-testid': 'DownloadReasonDialog-Reason',
        }}
      />
    </Dialog>
  );
};
